import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';

import { GUID, InternalUserProfile, PersonOrg } from '@ndvr/auth-api';

import { clearImpersonatedUser } from '@/components/ImpersonationBanner';
import { AuthApi } from 'API';
import { COGNITO_CC_REDIRECT_URI } from 'env';
import { FetcherError } from 'utils';

import { clearRumUser } from '../../datadog-logging';

export const useAssignedFlags = () =>
    useQuery({
        queryKey: ['person', 'featureFlags'],
        queryFn: async () => {
            const [response] = await AuthApi().getActiveFlags();
            return response ?? [];
        },
    });

export const useAuthorizeCognitoOAuthCode = () =>
    useMutation({
        mutationFn: async (code: string) => {
            const [login] = await AuthApi().loginWithCognitoAuthorizationCode({
                code: code,
                callbackUrl: COGNITO_CC_REDIRECT_URI,
            });
            return login?.loginResponse;
        },
    });

export const logout = async () => {
    try {
        // logout, also deletes session cookie
        await AuthApi().logout();
    } catch (e) {
        // no need to show this error to the user
    }

    // internal Auth logic is reading localStorage for 'isLoggedIn'
    localStorage.removeItem('auth_user_guid');

    clearImpersonatedUser();

    clearRumUser();

    // reload page, routing logic will handle navigating to login page upon reload
    location.reload();
};

export const useCurrentPersonOrgGUID = (options?: Partial<UseQueryOptions<GUID<PersonOrg> | null>>) =>
    useQuery({
        queryKey: ['person', 'currentPersonOrgGUID'],
        queryFn: async () => {
            try {
                const [response] = await AuthApi({ hideError: true }).getCurrentPersonOrgGUID();
                return response ?? null;
            } catch (error) {
                if (error instanceof FetcherError && error.status === 401) {
                    return null;
                }
                throw error;
            }
        },
        retry: false,
        meta: { skipUnauthorizedHandling: true },
        ...options,
    });

const toInternalUserProfileGUID = (personOrgGUID: GUID<PersonOrg>) =>
    personOrgGUID as unknown as GUID<InternalUserProfile>;

export const useCurrentInternalUserProfile = (
    personOrgGUID?: GUID<PersonOrg>,
    options?: Partial<UseQueryOptions<InternalUserProfile | undefined>>,
) =>
    useQuery({
        ...options,
        queryKey: ['internalUserProfile', personOrgGUID],
        queryFn: async () => {
            const [response] = await AuthApi().getInternalUserProfile(toInternalUserProfileGUID(personOrgGUID!));
            return response ?? undefined;
        },
        enabled: Boolean(personOrgGUID) && (options?.enabled ?? true),
    });

export const useUsersTenants = (personOrgGUID: GUID<PersonOrg>) =>
    useQuery({
        // while the query is not keyed to the personOrgGUID, it is still scoped to the user
        queryKey: ['person', personOrgGUID, 'tenants'],
        queryFn: async () => {
            const [tenants] = await AuthApi().getTenantsForLoggedInUser();
            return tenants ?? [];
        },
    });
